import { formatDateMMDDYYYY } from '../../../../utils/formatUtils';
import type { Game } from '../../../../types/features/games';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export type GameTableFilter = 'all' | 'current' | 'expired' | 'upcoming';
export type GameTableSortKey = 'game_id' | 'name' | 'cost_per_ticket' | 'tickets_per_pack' | 'dates';
export type GameTableSortDirection = 'asc' | 'desc';

export const formatGameCurrency = (amount: string | number): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return currencyFormatter.format(numericAmount);
};

export const formatGameDate = (dateString: string): string => {
  if (!dateString) {
    return '-';
  }

  try {
    return formatDateMMDDYYYY(dateString, '-');
  } catch {
    return '-';
  }
};

export const formatGameDateRange = (startDate: string | null, endDate: string | null): string => {
  const startLabel = formatGameDate(startDate || '');
  const endLabel = formatGameDate(endDate || '');

  if (startLabel === '-' && endLabel === '-') {
    return '-';
  }

  return `${startLabel} to ${endLabel}`;
};

export const formatPendingPluVerification = (pending: boolean | undefined): string => (
  pending ? 'Pending' : 'Done'
);

const parseGameDateValue = (dateString: string | null): number | null => {
  if (!dateString) {
    return null;
  }

  const dateOnly = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const parsedTime = new Date(`${dateOnly}T00:00:00`).getTime();

  return Number.isNaN(parsedTime) ? null : parsedTime;
};

export const getGameStatusFilter = (game: Game, now = new Date()): Exclude<GameTableFilter, 'all'> => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startTime = parseGameDateValue(game.start_date);
  const endTime = parseGameDateValue(game.end_date);

  if (startTime !== null && startTime > today) {
    return 'upcoming';
  }

  if (endTime !== null && endTime < today) {
    return 'expired';
  }

  return 'current';
};

export const matchesGameSearch = (game: Game, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return true;
  }

  return [
    game.game_id,
    game.name,
    game.plu,
  ].some((value) => String(value ?? '').toLowerCase().includes(normalizedQuery));
};

const getGameSortValue = (game: Game, sortKey: GameTableSortKey): string | number => {
  if (sortKey === 'game_id') {
    return game.game_id;
  }

  if (sortKey === 'name') {
    return game.name.toLowerCase();
  }

  if (sortKey === 'cost_per_ticket') {
    return Number(game.cost_per_ticket);
  }

  if (sortKey === 'tickets_per_pack') {
    return game.tickets_per_pack;
  }

  return parseGameDateValue(game.start_date) ?? Number.MAX_SAFE_INTEGER;
};

export const filterAndSortGames = (
  games: Game[],
  query: string,
  filter: GameTableFilter,
  sortKey: GameTableSortKey,
  sortDirection: GameTableSortDirection,
  now = new Date(),
): Game[] => {
  const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

  return games
    .filter((game) => matchesGameSearch(game, query))
    .filter((game) => filter === 'all' || getGameStatusFilter(game, now) === filter)
    .sort((left, right) => {
      const leftValue = getGameSortValue(left, sortKey);
      const rightValue = getGameSortValue(right, sortKey);

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return (leftValue - rightValue) * directionMultiplier;
      }

      return String(leftValue).localeCompare(String(rightValue)) * directionMultiplier;
    });
};
